import { ApolloClient, ApolloQueryResult } from 'apollo-client';
import { InMemoryCache } from 'apollo-cache-inmemory';
import apolloUploadClient from 'apollo-upload-client';
import { WebSocketLink } from 'apollo-link-ws';
import { split, ApolloLink, Observable, FetchResult } from 'apollo-link';
import { getMainDefinition } from 'apollo-utilities';
import Vue from 'vue';
import VueApollo from 'vue-apollo';
import { ErrorResponse, onError } from 'apollo-link-error';
import { OperationDefinitionNode } from 'graphql';
import { AuthQuery } from './api/session/graphql/session.queries';
import { EDocumentStatus } from './documents/types/enums/document-status';
import { actionSetPreviewExpired } from './store/actions';
import {
  EDocumentAction,
  documentNamespace,
} from '@signing-page/documents/store/document.constants';
import {
  actionLoadTokenSuccess,
  actionSetStatus,
} from '@signing-page/session/store/session.actions';
import { namespace as sessionNamespace } from '@signing-page/session/store/session.store';
import bugsnagClient from '@lib-shared/bugsnag';
import store from '@signing-page/store/store';

const getHTTPURI = options => {
  const { operationName } = options;
  if (process.env.VUE_APP_PUBLIC_API_BASE_URL) {
    // Use new Public API endpoint if it's specified
    return `https://${process.env.VUE_APP_PUBLIC_API_BASE_URL}/graphql?op=${operationName}`;
  }
  // VUE_APP_DEALROOM_URL is deprecated
  if (process.env.VUE_APP_DEALROOM_URL) {
    return `https://${process.env.VUE_APP_DEALROOM_URL}?op=${operationName}`;
  }
  return `http://localhost:3002/graphql?op=${operationName}`;
};

const getWSURI = () => {
  if (process.env.VUE_APP_PUBLIC_API_BASE_URL) {
    // Use new Internal API endpoint if it's specified
    return `wss://${process.env.VUE_APP_PUBLIC_API_BASE_URL}/graphql`;
  }
  // VUE_APP_DEALROOM_URL is deprecated
  if (process.env.VUE_APP_DEALROOM_URL) {
    return `wss://${process.env.VUE_APP_DEALROOM_URL}`;
  }
  return 'ws://localhost:3002/graphql';
};

const uploadClientLink = apolloUploadClient.createUploadLink({
  uri: getHTTPURI,
});

const wsLink = new WebSocketLink({
  uri: getWSURI(),
  options: {
    reconnect: true,
    lazy: true,
    connectionParams: () => ({ Authorization: getAuthToken() }),
  },
});

const errorLink = onError(({ graphQLErrors, networkError, operation, forward }: ErrorResponse) => {
  if (graphQLErrors) {
    const sessionExpiredErrors: any[] = graphQLErrors.filter(
      (error: any) => error.extensions && error.extensions.code === 'UNAUTHENTICATED'
    );
    const recalledError: boolean = graphQLErrors.some(
      (error: any) => error.extensions && error.extensions.code === 'RECALLED'
    );
    const signedDocumentError: boolean = graphQLErrors.some(
      (error: any) => error.message === 'Action not allowed for signed documents'
    );
    if (signedDocumentError) {
      store.dispatch(
        `${documentNamespace}/${[EDocumentAction.SetDocumentStatus]}`,
        EDocumentStatus.Signed
      );
    }
    if (recalledError) {
      store.dispatch(`${sessionNamespace}/${actionSetStatus}`, EDocumentStatus.Recalled);
    }
    const filteredErrors: any[] = graphQLErrors.filter(
      (error: any) =>
        !(error.extensions?.code === 'UNAUTHENTICATED' || error.extensions?.code === 'RECALLED')
    );
    if (sessionExpiredErrors.length && store.state.isPreview) {
      store.dispatch(actionSetPreviewExpired, true);
    } else if (sessionExpiredErrors.length) {
      return new Observable(
        (
          observer: ZenObservable.Observer<
            FetchResult<{ [key: string]: any }, Record<string, any>, Record<string, any>>
          >
        ) => {
          apolloClient
            .query({
              query: AuthQuery,
              variables: {
                documentId: store.state.documentId,
                recipientId: store.state.recipientId,
                authId: store.state.authId,
              },
            })
            .then((response: ApolloQueryResult<any>) => {
              store.dispatch(`${sessionNamespace}/${actionLoadTokenSuccess}`, {
                token: response.data.auth,
              });
              const subscriber = {
                next: observer.next.bind(observer),
                error: observer.error.bind(observer),
                complete: observer.complete.bind(observer),
              };
              forward(operation).subscribe(subscriber);
            })
            .catch(error => console.error('Token renegotation failed', error));
        }
      );
    }
    if (bugsnagClient) {
      filteredErrors.forEach(graphQLError => {
        bugsnagClient.notify(new Error(JSON.stringify(graphQLError)), event => {
          event.groupingHash = graphQLError.message;
          event.addMetadata('GraphQL', {
            operationName: operation?.operationName,
            variables: operation?.variables,
            type: (operation?.query?.definitions[0] as OperationDefinitionNode)?.operation,
          });
        });
      });
    }
  }
  if (networkError && bugsnagClient) {
    bugsnagClient.notify(new Error(JSON.stringify(networkError)), event => {
      event.groupingHash = JSON.stringify(networkError);
      event.addMetadata('GraphQL', {
        operationName: operation?.operationName,
        variables: operation?.variables,
        type: (operation?.query?.definitions[0] as OperationDefinitionNode)?.operation,
      });
    });
  }
});

const getAuthToken = () => {
  const { jwt = '' } = store.state['session'].token;
  return `Bearer ${jwt}`;
};

const breadcrumbLink = new ApolloLink((operation, forward) => {
  if (bugsnagClient) {
    bugsnagClient.leaveBreadcrumb(
      `GraphQL ${(operation.query.definitions[0] as OperationDefinitionNode).operation}: "${
        operation.operationName
      }"`,
      {
        name: operation.operationName,
        type: (operation.query.definitions[0] as OperationDefinitionNode).operation,
        variables: operation.variables,
      }
    );
  }
  return forward(operation);
});

const authLink = new ApolloLink((operation, forward) => {
  operation.setContext({ headers: { authorization: getAuthToken() } });
  return forward(operation);
});

const link = split(
  // split based on operation type
  ({ query }) => {
    const definition = getMainDefinition(query);
    return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
  },
  wsLink,
  uploadClientLink
);

export const apolloClient = new ApolloClient({
  link: ApolloLink.from([authLink, breadcrumbLink, errorLink, link]),
  cache: new InMemoryCache(),
});

Vue.use(VueApollo);

export const apolloProvider = new VueApollo({
  defaultClient: apolloClient,
});
